import React from "react";
import { Loader2 } from "lucide-react";
import { TRAIT_TYPES, TraitCategory } from "../data";
import { SectionHeader, primaryBtn } from "./ui";
import { useWarehouseCatalog, useWarehouseStatus } from "../warehouse-api";

// Confirmed live: GET /traits/warehouse/catalog?category=custom|computed|predictive
// — "Behavioral" in this UI is "computed" on the wire (matches the
// "Behavioral/Computed Trait" card title).
const CATEGORY_TO_API: Record<TraitCategory, "custom" | "computed" | "predictive"> = {
  Custom: "custom",
  Behavioral: "computed",
  Predictive: "predictive",
};

const compactCount = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K` : String(n));

const StepType: React.FC<{ category: TraitCategory | null; onSelect: (c: TraitCategory) => void; onNext: () => void }> = ({ category, onSelect, onNext }) => {
  const { data: warehouseStatus } = useWarehouseStatus();
  const warehouseAvailable = !!(warehouseStatus?.enabled && warehouseStatus?.configured);

  // One real, category-scoped fetch per card — replaces the old hardcoded
  // "Example Traits" chip list with the tenant's own traits and how many
  // profiles carry each one, per direct request. Falls back to the static
  // example list below for a tenant with no warehouse connected/enabled.
  const custom = useWarehouseCatalog(warehouseAvailable, { category: "custom", per_page: 6 });
  const computed = useWarehouseCatalog(warehouseAvailable, { category: "computed", per_page: 6 });
  const predictive = useWarehouseCatalog(warehouseAvailable, { category: "predictive", per_page: 6 });
  const catalogByCategory = { custom, computed, predictive };

  return (
    <div>
      <SectionHeader title="Choose the type of trait" subtitle="Start by selecting the type of trait you want to create. You can configure the details in the next step" />

      <p className="text-base text-gray-800 dark:text-gray-200 mb-3">Trait Type</p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {TRAIT_TYPES.map((t) => {
          const selected = category === t.category;
          const Icon = t.icon;
          const catalog = catalogByCategory[CATEGORY_TO_API[t.category]];
          const realTraits = catalog.data?.data || [];
          const showReal = warehouseAvailable && (catalog.loading || realTraits.length > 0);
          return (
            <button
              key={t.category}
              type="button"
              onClick={() => onSelect(t.category)}
              className={`text-left rounded-xl p-4 bg-white dark:bg-gray-900 transition-colors ${
                selected ? "border-2 border-indigo-700" : "border border-gray-300 dark:border-gray-700 hover:border-indigo-300"
              }`}
            >
              <span className="flex items-center gap-2">
                <Icon size={22} className="text-indigo-700 shrink-0" />
                <span className={`text-xl ${selected ? "text-gray-900 dark:text-gray-100" : "text-gray-500 dark:text-gray-400"}`}>{t.title}</span>
              </span>
              <span className="block text-sm text-gray-600 dark:text-gray-400 mt-1">{t.blurb}</span>
              <span className="block mt-2 rounded bg-indigo-50 dark:bg-indigo-500/10 px-2 py-1.5">
                <span className="block text-sm text-gray-900 dark:text-gray-100">Best for</span>
                <span className="block text-sm text-gray-700 dark:text-gray-300 mt-2">{t.bestFor}</span>
              </span>
              <span className="block text-sm text-gray-800 dark:text-gray-200 mt-3">{showReal ? "Traits in your data" : "Example Traits"}</span>
              {showReal ? (
                catalog.loading ? (
                  <span className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                    <Loader2 size={12} className="animate-spin" /> Loading…
                  </span>
                ) : (
                  <span className="block mt-1.5 space-y-1">
                    {realTraits.map((tr) => (
                      <span
                        key={tr.trait_name}
                        className="flex items-center justify-between gap-2 text-sm px-2 py-1 rounded bg-indigo-50 dark:bg-indigo-500/15 text-gray-800 dark:text-gray-100"
                      >
                        <span className="truncate">{tr.trait_name}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">{compactCount(tr.profile_count)} profiles</span>
                      </span>
                    ))}
                  </span>
                )
              ) : (
                <span className="flex flex-wrap gap-1.5 mt-1.5">
                  {t.examples.map((e, i) => (
                    <span key={`${e}-${i}`} className="text-sm px-2 py-1 rounded bg-indigo-50 dark:bg-indigo-500/15 text-gray-800 dark:text-gray-100">
                      {e}
                    </span>
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex justify-end mt-10">
        <button type="button" className={`${primaryBtn} disabled:opacity-50`} disabled={!category} onClick={onNext}>
          Continue to Configure
        </button>
      </div>
    </div>
  );
};

export default StepType;
